import Action from 'actions';
import RuleBuilder from 'services/simplex/rule-builder';
import {Simplex as BaseSimplex} from 'services/sports/default/simplex';
import {groupObjectsByProp} from 'helpers/array-helper';
import {getSimplexUnitName} from 'helpers/simplex-helper';

export class SimplexClassic extends BaseSimplex {
    limitPositions({incomingValues = {}}) {
        const {
            playersData: {preparedPlayers},
            sportConfig,
        } = Action.getState();
        const players = preparedPlayers.groupedBy.allowedPositions();
        const teams = groupObjectsByProp(players, 'team');
        const {unitIdPropName = 'playerId'} = incomingValues;

        const maxHittersFromOneTeam = sportConfig.getConfigProp('maxHittersFromOneTeam');
        const result = [];

        if (!maxHittersFromOneTeam) {
            return result;
        }

        Object.values(teams).map((players) => {
            const builder = RuleBuilder.fromAtMost(maxHittersFromOneTeam);

            players.map((player) => {
                if (!player.isPitcher()) {
                    builder.addVar(getSimplexUnitName(player[unitIdPropName]), 1);
                }
            });

            builder.isRuleCreatable() && result.push(builder.createRule());
        });

        return result;
    }

    team({incomingValues = {}}) {
        const {
            playersData: {preparedPlayers},
            opposingPitchers,
            sportConfig,
            lineupGenerator: {settings},
        } = Action.getState();
        const players = preparedPlayers.groupedBy.allowedPositions();
        const teams = groupObjectsByProp(players, 'team');
        const maxSingleTeamPlayers = sportConfig.getConfigProp('maxSingleTeamPlayers');
        const maxOpposingHittersQuantity = getMaxOpposingHittersQuantity();
        const minOpposingHittersQuantity = -1 * opposingPitchers.getPitchersQuantity('min');
        const {unitIdPropName = 'playerId'} = incomingValues;

        function isOpposingPitchersAllowed() {
            return opposingPitchers.isAllowed() || settings.isRelaxBuildRulesUsed();
        }

        function getPitchers(players) {
            return players.filter((player) => player.isPitcher());
        }

        function getHitters(players) {
            return players.filter((player) => !player.isPitcher());
        }

        function getMaxOpposingHittersQuantity() {
            return isOpposingPitchersAllowed()
                ? maxSingleTeamPlayers - opposingPitchers.getPitchersQuantity('max')
                : maxSingleTeamPlayers;
        }

        function createSingleTeamPlayersRuleBuilder(players) {
            const builder = RuleBuilder.fromAtMost(maxSingleTeamPlayers);

            players.map((player) => builder.addVar(getSimplexUnitName(player[unitIdPropName]), 1));

            return builder;
        }

        function createMaxOpposingHittersVsPitchersRuleBuilder(hitters, pitchers) {
            const builder = RuleBuilder.fromAtMost(maxSingleTeamPlayers);

            pitchers.map((player) => {
                builder.addVar(getSimplexUnitName(player[unitIdPropName]), maxOpposingHittersQuantity);
            });

            hitters.map((player) => {
                builder.addVar(getSimplexUnitName(player[unitIdPropName]), 1);
            });

            return builder;
        }

        function createMinOpposingHittersVsPitchersRuleBuilder(hitters, pitchers) {
            const builder = RuleBuilder.fromAtLeast(0);

            pitchers.map((player) => {
                builder.addVar(getSimplexUnitName(player[unitIdPropName]), minOpposingHittersQuantity);
            });

            hitters.map((player) => {
                builder.addVar(getSimplexUnitName(player[unitIdPropName]), 1);
            });

            return builder;
        }

        const result = [];
        Object.values(teams).map((players) => {
            if (!teams.hasOwnProperty(players[0].opponent)) {
                const builder = createSingleTeamPlayersRuleBuilder(players);
                builder.isRuleCreatable() && result.push(builder.createRule());
                return;
            }

            const pitchers = getPitchers(teams[players[0].opponent]);
            const hitters = getHitters(players);

            const builder = createMaxOpposingHittersVsPitchersRuleBuilder(hitters, pitchers);
            builder.isRuleCreatable() && result.push(builder.createRule());

            if (isOpposingPitchersAllowed()) {
                const builder = createMinOpposingHittersVsPitchersRuleBuilder(hitters, pitchers);
                builder.isRuleCreatable() && result.push(builder.createRule());
            }
        });

        return result;
    }
}
